import * as p from '@clack/prompts'
import type { VercelBlobFlowResult } from '@cli-types/next-adapter/vercel.js'
import { redactSecrets } from '@cli-utils/process.js'
import { spinner } from '@cli-utils/terminal.js'
import pc from 'picocolors'
import { ensureVercelAuth } from '../auth/ensure-vercel-auth.js'
import { guardEnvLocal } from '../env-guard.js'
import { resolveVercelRunner } from '../runner/resolve-vercel-runner.js'
import { authFailureMessage } from './auth-failure-message.js'
import { blobFailureMessage } from './blob-failure-message.js'
import { ensureLinkedProject } from './ensure-linked-project.js'
import { provisionBlobForMode } from './provision-blob-for-mode.js'

export async function runVercelBlobFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  env?: NodeJS.ProcessEnv
}): Promise<VercelBlobFlowResult> {
  const env = options.env ?? process.env
  const envGuard = guardEnvLocal(options.cwd)

  try {
    const runnerSpinner = spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.clear()

    const auth = await ensureVercelAuth(runner, options.cwd, {
      env,
      quietIfAuthed: true,
      allowLogin: options.interactive
    })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }

    await ensureLinkedProject(runner, options.cwd, options.projectName, env, auth.username)

    const blob = await provisionBlobForMode(runner, options)
    if (blob.failure || !blob.token) {
      p.log.warn(blobFailureMessage(blob.failure))
      if (blob.detail) p.log.message(pc.dim(redactSecrets(blob.detail)))
      return { ok: false }
    }

    return {
      ok: true,
      token: blob.token,
      blobStoreName: blob.storeName
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  } finally {
    envGuard.restore()
  }
}
